// Defensive shim for browser-translation-induced React crashes.
//
// Browser translators (Chrome/Edge Google Translate, Edge built-in, Firefox 118+
// built-in, Safari built-in) wrap text nodes in extra elements (typically
// `<font>` tags) after React commits. When React later removes or replaces
// those nodes, the DOM has diverged from React's fiber tree and `removeChild` /
// `insertBefore` throw NotFoundError, killing the page.
//
// We patch the two DOM methods so that when the expected child is no longer a
// direct child of `this`, we walk up to find the translator-inserted wrapper
// that IS a direct child, and operate on that instead. The orphaned wrapper is
// removed/replaced, so live-updating UI (search results, lists, etc.) stays in
// sync with React's view and the translator re-processes new content.
//
// See https://github.com/facebook/react/issues/11538 for the canonical write-up.

let installed = false;

function findChildAncestor(node: Node | null, expectedParent: Node): Node | null {
  let cursor: Node | null = node;
  while (cursor && cursor.parentNode !== expectedParent) {
    cursor = cursor.parentNode;
  }
  return cursor;
}

export function installTranslationCrashShim(): void {
  if (installed || typeof Node === 'undefined') return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      const wrapper = findChildAncestor(child.parentNode, this);
      if (wrapper) {
        originalRemoveChild.call(this, wrapper);
        return child;
      }
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[translation-shim] removeChild: no ancestor wrapper found, skipping', child);
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  } as typeof Node.prototype.removeChild;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      const wrapper = findChildAncestor(referenceNode.parentNode, this);
      if (wrapper) {
        return originalInsertBefore.call(this, newNode, wrapper) as T;
      }
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[translation-shim] insertBefore: no ancestor wrapper found, appending', newNode);
      }
      this.appendChild(newNode);
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  } as typeof Node.prototype.insertBefore;
}
